import PageMeta from "@/components/PageMeta";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  MessageSquare,
  PencilRuler,
  Factory,
  Truck,
  MapPin,
  Clock,
  Shield,
  Handshake,
  Image,
  FileText,
  Palette,
  Camera,
} from "lucide-react";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4a60d089-16ce-43a5-b375-972c21245635/images/0201f99be3194a155bf43dba77a5a9c22bdee8afeb9d262f16b06ac5359457eb.png";

const offerings = [
  "Custom Canvas Prints",
  "Bespoke Wall Murals",
  "Business Signage",
  "Window Graphics",
  "Vehicle Wraps & Decals",
  "Banners & Displays",
];

const steps = [
  {
    icon: MessageSquare,
    title: "Tell Us What You Need",
    desc: "Fill in our quote form with your project details, dimensions, and any reference material. We respond within 24 hours.",
  },
  {
    icon: PencilRuler,
    title: "We Design & Confirm",
    desc: "We create a mockup or proof for your approval. Revisions are included \u2014 nothing goes to print until you\u2019re happy.",
  },
  {
    icon: Factory,
    title: "Premium Production",
    desc: "Your order is produced using professional-grade printers, inks, and materials. Every piece is quality-checked.",
  },
  {
    icon: Truck,
    title: "Delivery or Installation",
    desc: "Collected, shipped, or professionally installed \u2014 whatever your project needs. We handle the last mile.",
  },
];

const artworkHelp = [
  { icon: Image, text: "A rough sketch or hand-drawn idea" },
  { icon: FileText, text: "A logo file, even low resolution" },
  { icon: Camera, text: "A photo of the space or reference" },
  { icon: Palette, text: "Just a description \u2014 we\u2019ll design it" },
];

const trust = [
  { icon: MapPin, label: "UK Based" },
  { icon: Shield, label: "Quality Guaranteed" },
  { icon: Clock, label: "5\u20137 Day Turnaround" },
  { icon: Handshake, label: "No-Obligation Quotes" },
];

export default function CustomOrderLanding() {
  const location = useLocation();

  return (
    <>
      <PageMeta
        title="Custom Order"
        description="Fully bespoke print and signage projects. Shop signs, large-format display, custom runs, and anything outside the standard categories. Describe your brief — we'll come back with a quote."
      />
      {/* ─── HERO ─── */}
      <section
        className="relative min-h-[80vh] flex items-end pb-20 md:pb-28"
        data-testid="custom-order-hero"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Custom signage craftsmanship"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-[#0A0D14]/80 to-[#0A0D14]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/30 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <p className="text-[11px] tracking-[0.35em] uppercase text-brand-gold font-body opacity-0 animate-fade-in-up">
            Custom Orders &mdash; UK Based
          </p>
          <h1
            className="mt-4 font-heading text-[clamp(2.25rem,5.5vw,4.5rem)] tracking-tight leading-[0.92] text-brand-text max-w-[600px] opacity-0 animate-fade-in-up"
            style={{ animationDelay: "100ms" }}
            data-testid="custom-heading"
          >
            Your Vision,
            <br />
            Our Craft
          </h1>
          <p
            className="mt-4 text-brand-text-secondary text-[15px] max-w-md font-body leading-relaxed opacity-0 animate-fade-in-up"
            style={{ animationDelay: "200ms" }}
          >
            Bespoke prints, signage, murals, and graphics &mdash; built to your
            exact spec by a UK team that works with you from brief to
            installation.
          </p>
          <div
            className="mt-8 flex flex-wrap gap-3 opacity-0 animate-fade-in-up"
            style={{ animationDelay: "300ms" }}
          >
            <Link
              to="/request-quote"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="hero-quote-cta"
            >
              Get a Free Quote <ArrowRight size={15} />
            </Link>
            <Link
              to="/canvas-category-landing"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 border border-white/20 text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="hero-browse-cta"
            >
              Browse Our Range
            </Link>
          </div>
        </div>
      </section>

      {/* ─── WHAT WE MAKE ─── */}
      <section
        className="py-20 md:py-24 max-w-7xl mx-auto px-6 md:px-12"
        data-testid="what-we-make-section"
      >
        <SectionFadeIn>
          <div className="max-w-lg">
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              What We Produce
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text leading-tight">
              If It Goes on a Wall, Window,
              <br className="hidden sm:block" /> or Vehicle &mdash; We Make It
            </h2>
          </div>
        </SectionFadeIn>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {offerings.map((item, i) => (
            <SectionFadeIn key={item} delay={i * 60}>
              <div
                className="flex items-center gap-3 px-5 py-4 bg-brand-surface/40 border border-brand-border/30 rounded-sm"
                data-testid={`offering-${i}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-brand-gold shrink-0" />
                <span className="text-[13px] text-brand-text font-medium">
                  {item}
                </span>
              </div>
            </SectionFadeIn>
          ))}
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section
        className="py-24 md:py-28 bg-brand-surface/30"
        data-testid="process-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionFadeIn>
            <div className="max-w-lg">
              <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
                How It Works
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text leading-tight">
                Four Steps, No Surprises
              </h2>
              <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed">
                Every custom project follows the same clear path. You stay in
                control at every stage.
              </p>
            </div>
          </SectionFadeIn>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
            {steps.map((s, i) => (
              <SectionFadeIn key={s.title} delay={i * 120}>
                <div data-testid={`process-step-${i + 1}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl font-heading text-brand-gold/20 leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <s.icon
                      size={20}
                      strokeWidth={1.6}
                      className="text-brand-gold"
                    />
                  </div>
                  <h3 className="font-body text-[14px] font-semibold text-brand-text mb-2">
                    {s.title}
                  </h3>
                  <p className="text-[13px] text-brand-text-secondary leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </SectionFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ARTWORK HELP ─── */}
      <section
        className="py-20 md:py-24 max-w-7xl mx-auto px-6 md:px-12"
        data-testid="artwork-help-section"
      >
        <div className="max-w-3xl">
          <SectionFadeIn>
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              Don&rsquo;t Have Artwork Ready?
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text leading-tight">
              We Work With What You Have
            </h2>
            <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed max-w-xl">
              You don&rsquo;t need a finished design to get started. Send us
              whatever you have and we&rsquo;ll take it from there.
            </p>
          </SectionFadeIn>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {artworkHelp.map((a) => (
              <SectionFadeIn key={a.text} delay={artworkHelp.indexOf(a) * 80}>
                <div
                  className="flex items-center gap-4 px-6 py-5 bg-brand-surface border border-brand-border rounded-sm"
                  data-testid={`artwork-help-${artworkHelp.indexOf(a)}`}
                >
                  <a.icon
                    size={20}
                    strokeWidth={1.6}
                    className="text-brand-gold shrink-0"
                  />
                  <span className="text-[13px] text-brand-text leading-snug">
                    {a.text}
                  </span>
                </div>
              </SectionFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TRUST STRIP ─── */}
      <section
        className="border-y border-white/[0.05] bg-brand-surface/40"
        data-testid="trust-strip"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-8">
            {trust.map((t) => (
              <div
                key={t.label}
                className="flex items-center gap-3"
                data-testid={`trust-${t.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <t.icon
                  size={18}
                  strokeWidth={1.6}
                  className="text-brand-gold shrink-0"
                />
                <span className="text-[13px] text-brand-text-secondary font-medium tracking-wide">
                  {t.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section
        className="py-20 md:py-24 max-w-7xl mx-auto px-6 md:px-12"
        data-testid="final-cta-section"
      >
        <SectionFadeIn>
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              Start Here
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text leading-tight">
              Tell Us What You Need
            </h2>
            <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed max-w-lg mx-auto">
              Fill in our quote form with your project details. We&rsquo;ll come
              back with a clear price and timeline &mdash; no obligation, no
              pressure. Most quotes returned within 24 hours.
            </p>
            <Link
              to="/request-quote"
              className="inline-flex items-center gap-2 mt-7 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-9 py-4 font-medium text-[14px] tracking-wide"
              data-testid="final-quote-cta"
            >
              Request a Free Quote <ArrowRight size={15} />
            </Link>
          </div>

          {/* secondary browse path */}
          <div className="mt-12 pt-8 border-t border-white/[0.05] flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <span className="text-[12px] text-brand-text-secondary/50 tracking-wide">
              Or browse what we already have in stock:
            </span>
            <div className="flex items-center gap-4">
              <Link
                to="/canvas-category-landing"
                className="text-[12px] text-brand-text-secondary hover:text-brand-gold transition-colors duration-300 tracking-wide font-medium"
                data-testid="browse-canvas-link"
              >
                Canvas Prints
              </Link>
              <span className="text-brand-border text-[10px]">/</span>
              <Link
                to="/wall-murals-landing"
                className="text-[12px] text-brand-text-secondary hover:text-brand-gold transition-colors duration-300 tracking-wide font-medium"
                data-testid="browse-murals-link"
              >
                Wall Murals
              </Link>
            </div>
          </div>
        </SectionFadeIn>
      </section>
    </>
  );
}
